import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import {
    DefaultCell,
    DefaultCellRightAlign,
    IndexCell,
    KeywordAnalysisPosition,
    SearchKeywordCell,
    TrafficShareWithVisits,
    TrendCell,
    UrlCellWebsiteAnalysis,
    WaKeywordPosition,
} from "app/components/React/Table/cells";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { SerpTableCellSingle } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellSingle";
import { CompareBarCell } from "app/components/React/Table/cells/CompareBarCell";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { NewAndLostChangeCell } from "app/components/React/Table/cells/NewAndLostChangeCell";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import { SelectAllRowsHeaderCellConsumer } from "app/components/React/Table/headerCells/SelectAllRowsHeaderCell";
import { DrilldownWrapper, StyledTrendContainer } from "app/components/keyword-research/landing-pages/styledComponents";
import {
    StyledEnrichButton,
    StyledEnrichButtonText,
} from "app/pages/keyword-research/website-analysis/pages/landing-pages/StyledComponents";
import { swNumberFilter } from "app/filters/ngFilters";
import type { RawParams } from "@uirouter/react";
import { ChangeStateCell } from "app/components/React/Table/cells/ChangeStateCell";
import { isEmpty } from "lodash";
import classNames from "classnames";
import DurationService from "app/services/DurationService";
import styled from "styled-components";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE, THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE } from "app/filters/const";
import { RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { ELandingPagesTabs } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import { AdColumnUnit } from "app/components/keyword-research/AdColumnUnit";
import { SimpleIconCell } from "app/components/React/Table/cells/SimpleIconCell";
import { SHOW_PAID_LANDING_PAGE_AD_SPEND_COLUMN } from "app/pages/website-analysis/traffic-sources/search/common/showPaidLandingPageAdSpendColumn";
import { allTrackers } from "app/services/track/track";

export const DEFAULT_SORT = "ClicksShare";
const DEFAULT_SORT_DIRECTION = "desc";

export const tabsMapping = { Trend: 0, Keywords: 1, Ads: 2 };

const i18n = i18nFilter();

const AlignRight = styled.div`
    text-align: right;
`;

export const getTableColumns = (params: RawParams, setTabIndex: (index: number) => void) => {
    const { country, webSource, duration, isWWW, selectedPageTab } = params;
    const durationObject = DurationService.getDurationData(duration);
    const isWindow = durationObject.forAPI.isWindow;
    const isPaidTab = selectedPageTab === ELandingPagesTabs[ELandingPagesTabs.Paid];

    return [
        {
            fixed: true,
            cellComponent: IndexCell,
            disableHeaderCellHover: true,
            sortable: false,
            width: 46,
            isResizable: false,
            hideInDashboard: true,
        },
        {
            field: "Url",
            displayName: i18n("organic.landing.pages.table.columns.pageUrl.title"),
            tooltip: i18n("organic.landing.pages.table.columns.pageUrl.title.tooltip"),
            cellComponent: UrlCellWebsiteAnalysis,
            cellClass: "url-cell",
            width: 248,
            sortable: false,
            showTotalCount: true,
        },
        {
            field: "ClicksShare",
            displayName: i18n("keywords.by.website.table.columns.clicks.title"),
            tooltip: i18n("organic.landing.pages.2.0.table.clicks.column.tooltip"),
            cellComponent: (props) => (
                <TrafficShareWithVisits
                    {...props}
                    applyAbbrNumberFilter
                    totalVisitsRowProp="Clicks"
                    thresholdForTotalFilter={50}
                    thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
                    displayLessThanThreshold={true}
                />
            ),
            sortable: true,
            width: 134,
            isResizable: true,
        },
        {
            field: "ClicksChange",
            width: 90,
            isResizable: true,
            displayName: i18n("organic.landing.pages.v2.table.columns.change.title"),
            tooltip: i18n(
                isWindow
                    ? "organic.landing.pages.v2.table.columns.window.change.tooltip"
                    : "organic.landing.pages.v2.table.columns.change.title.tooltip",
            ),
            headerComponent: DefaultCellHeaderRightAlign,
            cellComponent: (props) => {
                const { PrevClicks, Clicks, ClicksChange, ChangeState } = props.row;
                const absChange = Math.abs(ClicksChange);
                // according to DMI-4654
                const precision = absChange > 0 && absChange < 0.1 ? 1 : 0;

                return (
                    <ChangeStateCell
                        {...props}
                        value={{
                            first: PrevClicks,
                            last: Clicks,
                            change: ClicksChange,
                            changeState: ChangeState,
                        }}
                        undefinedSign={i18n("common.undefined.text")}
                        isPercentage={true}
                        cellFilter={`simplePercentage:${precision}`}
                    />
                );
            },
            sortable: true,
        },
        {
            field: "Trend",
            width: 188,
            isResizable: true,
            displayName: i18n("organic.landing.pages.v2.table.columns.clicks-trend.title"),
            tooltip: i18n("organic.landing.pages.2.0.table.clicks-trend.column.tooltip"),
            headerComponent: DefaultCellHeader,
            cellComponent: (props) => {
                const isDisabled = isEmpty(props.value);

                const onClick = (e: Event) => {
                    allTrackers.dispatchTrackEvent("Button", "click", "Organic Pages V2/trend drilldown");
                    e.stopPropagation();
                    setTabIndex(tabsMapping.Trend);
                    props.onCellClick(e, props.row?.index);
                };

                return (
                    <DrilldownWrapper>
                        {isDisabled ? (
                            <AlignRight>{i18n("common.undefined.text")}</AlignRight>
                        ) : (
                            <TrendCell
                                {...props}
                                thresholdMinValue={THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE}
                                Container={StyledTrendContainer}
                            />
                        )}
                        <StyledEnrichButton
                            height={24}
                            onClick={isDisabled ? undefined : onClick}
                            className={classNames("enrich", isDisabled && "enrich--disabled")}
                            iconName="chev-down"
                            isDisabled={isDisabled}
                        >
                            <StyledEnrichButtonText isDisabled={isDisabled}>
                                {i18n("organic.landing.pages.table.see.trend.enrich.button.title")}
                            </StyledEnrichButtonText>
                        </StyledEnrichButton>
                    </DrilldownWrapper>
                );
            },
            sortable: false,
        },
        ...(isPaidTab && SHOW_PAID_LANDING_PAGE_AD_SPEND_COLUMN
            ? [
                  {
                      field: "UrlSpend",
                      displayName: i18n("paid.landing.pages.table.columns.urlspend.title"),
                      tooltip: i18n("paid.landing.pages.table.columns.urlspend.title.tooltip"),
                      headerComponent: DefaultCellHeaderRightAlign,
                      cellComponent: ({ value }) => (
                          <RightFlexRow>{!value ? i18n("common.undefined.text") : `$${value}`}</RightFlexRow>
                      ),
                      width: 84,
                      sortable: true,
                  },
              ]
            : []),
        webSource === devicesTypes.TOTAL
            ? {
                  field: "WebSourceShare",
                  displayName: i18n("mobile.v2.table.columns.websource.share.title"),

                  cellComponent: (props) => {
                      if (props.row.Clicks > 0) {
                          return <CompareBarCell {...props} />;
                      } else {
                          return <AlignRight>{i18n("common.undefined.text")}</AlignRight>;
                      }
                  },
                  headerComponent: DefaultCellHeader,
                  tooltip: i18n("mobile.v2.column.table.header.websource.share.tooltip"),
                  width: 120,
              }
            : null,
        ...(country !== "999"
            ? [
                  // {
                  //     field: "Position",
                  //     displayName: i18n("organic.landing.pages.v2.table.columns.position.title"),
                  //     tooltip: i18n(
                  //         "organic.landing.pages.v2.table.columns.position.title.tooltip",
                  //     ),
                  //     cellComponent: (props) => {
                  //         const row = { ...props.row };
                  //         row.CurrentPosition = row.Position;
                  //         row.PreviousPosition = row.PrevPosition;
                  //         const newProps = {
                  //             ...props,
                  //             row,
                  //             showTooltip: true,
                  //         };
                  //         return <KeywordAnalysisPosition {...newProps} />;
                  //     },
                  //     headerComponent: DefaultCellHeaderRightAlign,
                  //     sortable: false,
                  // },
                  {
                      field: "PositionOverall",
                      displayName: i18n("organic.landing.pages.v2.table.columns.position.overall.title"),
                      tooltip: i18n("organic.landing.pages.v2.table.columns.position.overall.title.tooltip"),
                      cellComponent: (props) => {
                          const row = { ...props.row };
                          row.CurrentPosition = row.PositionOverall;
                          row.PreviousPosition = row.PrevPositionOverall;
                          const newProps = {
                              ...props,
                              row: row,
                              showTooltip: true,
                              decimal: 1,
                          };
                          return <KeywordAnalysisPosition {...newProps} />;
                      },
                      headerComponent: DefaultCellHeaderRightAlign,
                      width: 90,
                      sortable: true,
                  },
              ]
            : []),
        {
            field: "KeywordsCount",
            displayName: i18n("organic.landing.pages.table.columns.keywordsNum.title"),
            tooltip: i18n(
                isWindow
                    ? "organic.landing.pages.2.0.table.window.keywords.column.tooltip"
                    : "organic.landing.pages.2.0.table.keywords.column.tooltip",
            ),
            cellComponent: ({ onCellClick, value, ...props }) => {
                const onClick = (e: Event) => {
                    allTrackers.dispatchTrackEvent("Button", "click", "Organic Pages V2/keywords drilldown");
                    e.stopPropagation();
                    setTabIndex(tabsMapping.Keywords);
                    onCellClick(e, props.row?.index);
                };
                return (
                    <DrilldownWrapper>
                        <DefaultCell value={swNumberFilter()(value)} />
                        {!props.row.parent && (
                            <StyledEnrichButton
                                height={24}
                                onClick={onClick}
                                className={"enrich"}
                                iconName={"chev-down"}
                                isDisabled={false}
                            >
                                <StyledEnrichButtonText isDisabled={false}>
                                    {i18n("organic.landing.pages.table.enrich.button.title")}
                                </StyledEnrichButtonText>
                            </StyledEnrichButton>
                        )}
                    </DrilldownWrapper>
                );
            },
            width: 170,
            sortable: true,
        },
        {
            field: "SearchTerm",
            displayName: i18n("organic.landing.pages.table.columns.topKeyword.title"),
            tooltip: i18n("organic.landing.pages.2.0.table.top.keyword.column.tooltip"),
            cellComponent: ({ value, ...props }) => {
                const isDisabled = value === "grid.upgrade";
                const keyword = isDisabled ? i18n("organic.landing.pages.table.columns.topKeyword.title") : value;
                return (
                    <SearchKeywordCell
                        {...(props as Omit<ITableCellProps, "value">)}
                        adsUrl={proNavigator.href("paidsearch_keywordAnalysis_serp_ads", {
                            webSource,
                            country,
                            duration,
                            isWWW,
                            keyword,
                        })}
                        disabled={isDisabled}
                        value={keyword}
                        destinationState={"organicsearch_keywordAnalysis_2"}
                        tooltipContent={translate("organic.landing.pages.table.columns.topKeyword.tooltip")}
                    />
                );
            },
            minWidth: 160,
            maxWidth: 190,
            sortable: false,
        },
        ...(isPaidTab
            ? [
                  {
                      field: "AdsCount",
                      displayName: i18n("organic.landing.pages.table.columns.ads.title"),
                      tooltip: i18n("organic.landing.pages.2.0.table.ads.column.tooltip"),
                      cellComponent: ({ onCellClick, value, ...props }) => {
                          const onClick = (e: Event) => {
                              allTrackers.dispatchTrackEvent("Button", "click", "Organic Pages V2/ads drilldown");
                              e.stopPropagation();
                              setTabIndex(tabsMapping.Ads);
                              onCellClick(e, props.row?.index);
                          };
                          return (
                              <DrilldownWrapper>
                                  <DefaultCell value={swNumberFilter()(value)} />
                                  {!props.row.parent && (
                                      <StyledEnrichButton
                                          height={24}
                                          onClick={onClick}
                                          className={"enrich"}
                                          iconName={"chev-down"}
                                          isDisabled={false}
                                      >
                                          <StyledEnrichButtonText isDisabled={false}>
                                              {i18n("organic.landing.pages.table.ads.enrich.button.title")}
                                          </StyledEnrichButtonText>
                                      </StyledEnrichButton>
                                  )}
                              </DrilldownWrapper>
                          );
                      },
                      width: 128,
                      sortable: true,
                  },
              ]
            : []),
        {
            field: "SerpFeatures",
            displayName: i18n("serp.column.name"),
            tooltip: i18n("organic.landing.pages.2.0.table.serp.column.tooltip"),
            headerComponent: DefaultCellHeader,
            cellComponent: SerpTableCellSingle,
            width: 120,
            sortable: false,
            isResizable: false,
        },
    ]
        .filter((column) => column)
        .map((col) => {
            const isSorted = col?.field === DEFAULT_SORT;
            return {
                ...col,
                isSorted,
                sortDirection: DEFAULT_SORT_DIRECTION,
                isResizable: col?.isResizable !== false,
                visible: true,
            };
        });
};

export const getKeywordsDrilldownTableColumns = (
    pageParams,
    sortField: string,
    sortDirection: string,
    keywordTargetState: string,
    _parentUrl?: string,
) => {
    const durationObject = DurationService.getDurationData(pageParams.duration);
    const isWindow = durationObject.forAPI.isWindow;

    return [
        {
            fixed: true,
            cellComponent: RowSelectionConsumer,
            sortable: false,
            headerComponent: SelectAllRowsHeaderCellConsumer,
            isResizable: false,
            width: 40,
            visible: true,
        },
        {
            fixed: true,
            cellComponent: IndexCell,
            disableHeaderCellHover: true,
            sortable: false,
            width: 40,
            isResizable: false,
        },
        {
            field: "SearchTerm",
            displayName: i18n("organic.landing.pages.enriched.table.columns.keyword.title"),
            tooltip: i18n("organic.landing.pages.enriched.table.columns.keyword.title.tooltip"),
            cellComponent: (props) => {
                return (
                    <SearchKeywordCell
                        {...props}
                        destinationState={keywordTargetState}
                        adsUrl={proNavigator.href(keywordTargetState, {
                            webSource: pageParams.webSource,
                            country: pageParams.country,
                            duration: pageParams.duration,
                            keyword: props.value,
                            selectedPageTab: pageParams.selectedPageTab,
                        })}
                    />
                );
            },
            sortable: false,
            showTotalCount: true,
            minWidth: 300,
            maxWidth: 400,
        },
        {
            field: "ClicksShare",
            displayName: i18n("organic.landing.pages.2.0.keywords.table.clicks.column.title"),
            tooltip: i18n("organic.landing.pages.2.0.table.clicks.column.tooltip"),
            cellComponent: (props) => (
                <TrafficShareWithVisits
                    {...props}
                    applyAbbrNumberFilter
                    totalVisitsRowProp="Clicks"
                    thresholdForTotalFilter={50}
                    thresholdTrafficShareWithVisits={THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE}
                    thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
                    displayLessThanThreshold={true}
                />
            ),
            sortable: true,
            width: 150,
            headerCellClass: "no-right-border",
        },
        {
            field: "ClicksChange",
            minWidth: 90,
            maxWidth: 110,
            isResizable: true,
            displayName: i18n("organic.landing.pages.v2.table.columns.change.title"),
            tooltip: i18n(
                isWindow
                    ? "organic.landing.pages.v2.drilldown.table.columns.window.change.tooltip"
                    : "organic.landing.pages.2.0.keywords.table.clicks-change.column.tooltip",
            ),
            headerComponent: DefaultCellHeaderRightAlign,
            cellComponent: (props) => {
                const { PreviousClicks, Clicks, ClicksChange } = props.row;
                const absChange = Math.abs(ClicksChange);
                /** @see {https://similarweb-rnd.atlassian.net/browse/DMI-4654} */
                const precision = absChange > 0 && absChange < 0.1 ? 1 : 0;
                return (
                    <NewAndLostChangeCell
                        {...props}
                        value={{
                            first: PreviousClicks,
                            last: Clicks,
                            change: ClicksChange,
                        }}
                        undefinedSign={i18n("common.undefined.text")}
                        isPercentage={true}
                        cellFilter={`simplePercentage:${precision}`}
                    />
                );
            },
            sortable: true,
        },
        pageParams.webSource === devicesTypes.TOTAL
            ? {
                  field: "WebSourceShare",
                  displayName: i18n("mobile.v2.table.columns.websource.share.title"),
                  cellComponent: (props) => {
                      if (props.value > 0) {
                          return <CompareBarCell {...props} />;
                      } else {
                          return <AlignRight>{i18n("common.undefined.text")}</AlignRight>;
                      }
                  },
                  headerComponent: DefaultCellHeader,
                  tooltip: i18n("organic.landing.pages.2.0.keywords.table.desktop-mobile.column.tooltip"),
                  width: 120,
              }
            : null,
        ...(pageParams.country !== "999"
            ? [
                  {
                      field: "PositionObj",
                      displayName: i18n("organic.landing.pages.2.0.enriched.table.position.column.title"),
                      tooltip: i18n("organic.landing.pages.2.0.enriched.table.position.column.title.tooltip"),
                      cellComponent: WaKeywordPosition,
                      headerComponent: DefaultCellHeaderRightAlign,
                      format: "minusNAFilter",
                      sortable: true,
                      width: 70,
                      headerCellClass: "no-right-border",
                  },
                  {
                      field: "PreviousPosition",
                      cellComponent: (props) => {
                          const PreviousPosition = props.value;
                          const { Position } = props.row;
                          const PositionChangeObj = {
                              first: PreviousPosition,
                              last: Position,
                              change: PreviousPosition - Position,
                          };

                          return (
                              <NewAndLostChangeCell
                                  {...props}
                                  undefinedSign={i18n("common.undefined.text")}
                                  isPercentage={false}
                                  value={PositionChangeObj}
                              />
                          );
                      },
                      cellFilter: "number:0",
                      headerComponent: DefaultCellHeaderRightAlign,
                      displayName: i18n("keywords.by.website.table.columns.pos.change.title"),
                      tooltip: "organic.landing.pages.2.0.keywords.table.pos-change.column.tooltip",
                      width: 90,
                      sortable: false,
                  },
              ]
            : []),
        {
            field: "Volume",
            displayName: i18n(
                isWindow
                    ? "organic.landing.pages.v2.enriched.table.columns.window.volume.title"
                    : "organic.landing.pages.enriched.table.columns.volume.title",
            ),
            tooltip: i18n(
                isWindow
                    ? "organic.landing.pages.v2.enriched.table.columns.window.volume.tooltip"
                    : "organic.landing.pages.enriched.table.columns.volume.title.tooltip",
            ),
            headerComponent: DefaultCellHeaderRightAlign,
            cellComponent: DefaultCellRightAlign,
            format: "minAbbrNumber:50",
            sortable: true,
            minWidth: 90,
            maxWidth: 120,
        },
        {
            field: "AverageVolume",
            displayName: i18n("organic.landing.pages.v2.enriched.table.columns.average.volume.title"),
            tooltip: i18n("organic.landing.pages.v2.enriched.table.columns.average.volume.title.tooltip"),
            headerComponent: DefaultCellHeaderRightAlign,
            cellComponent: DefaultCellRightAlign,
            format: "minAbbrNumber:50",
            sortable: false,
            minWidth: 90,
            maxWidth: 120,
        },

        {
            field: "Cpc",
            displayName: i18n("organic.landing.pages.enriched.table.columns.cpc.title"),
            tooltip: i18n("organic.landing.pages.enriched.table.columns.cpc.title.tooltip"),
            headerComponent: DefaultCellHeaderRightAlign,
            cellComponent: DefaultCellRightAlign,
            format: "CPC",
            sortable: false,
            minWidth: 90,
            maxWidth: 120,
        },
        {
            field: "SiteSerpFeatures",
            displayName: i18n("serp.column.name"),
            tooltip: i18n("organic.landing.pages.2.0.keywords.table.serp.column.tooltip"),
            headerComponent: DefaultCellHeader,
            cellComponent: SerpTableCellSingle,
            width: 140,
            sortable: false,
            isResizable: false,
        },
    ]
        .filter((column) => column)
        .map((col) => {
            const isSorted = col.field === sortField;
            return {
                ...col,
                isSorted,
                sortDirection: sortDirection ?? DEFAULT_SORT_DIRECTION,
                isResizable: col.isResizable !== false,
                visible: true,
            };
        });
};

export const adsDrilldownTableColumns = [
    {
        fixed: true,
        cellComponent: IndexCell,
        disableHeaderCellHover: true,
        sortable: false,
        width: 40,
        isResizable: false,
        cellClass: "indexCell",
    },
    {
        field: "AdType",
        displayName: i18n("organic.landing.pages.2.0.ads.table.ad_type.column.title"),
        tooltip: i18n("organic.landing.pages.2.0.ads.table.ad_type.column.tooltip"),
        cellComponent: SimpleIconCell,
        width: 72,
        sortable: false,
        isResizable: false,
    },
    {
        field: "AdDetails",
        get displayName() {
            return translate("organic.landing.pages.2.0.ads.table.addetails.column.title");
        },
        get tooltip() {
            return translate("organic.landing.pages.2.0.ads.table.addetails.column.tooltip");
        },
        cellComponent: AdColumnUnit,
        minWidth: 430,
        showTotalCount: true,
        cellClass: "kwa-ads-ads",
        isResizable: true,
        sortable: false,
    },

    {
        field: "ClicksShare",
        displayName: i18n("organic.landing.pages.2.0.keywords.table.clicks.column.title"),
        tooltip: i18n("organic.landing.pages.2.0.table.clicks.column.tooltip"),
        cellComponent: (props) => (
            <TrafficShareWithVisits
                {...props}
                applyAbbrNumberFilter
                totalVisitsRowProp="Clicks"
                thresholdForTotalFilter={50}
                thresholdTrafficShareWithVisits={THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE}
                thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
                displayLessThanThreshold={true}
            />
        ),
        sortable: true,
        width: 150,
        headerCellClass: "no-right-border",
        isResizable: false,
    },
]
    .filter((column) => column)
    .map((col) => {
        const isSorted = col.field === DEFAULT_SORT;
        return {
            ...col,
            isSorted,
            sortDirection: DEFAULT_SORT_DIRECTION,
            visible: true,
        };
    });
